import { useState } from 'react';
import './StorefrontView.css';

const ANTHROPIC_API = 'https://api.anthropic.com/v1/messages';

function Modal({ title, item, fields, onSubmit, onClose }) {
  const [values, setValues] = useState({ name: '', email: '', phone: '', message: '' });
  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.name.trim() || !values.email.trim()) return;
    onSubmit(values);
  };
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p className="modal-item-name">"{item?.title}" — ${Number(item?.price).toFixed(2)}</p>
        <form onSubmit={handleSubmit}>
          <input className="field-input" placeholder="Your name *" value={values.name} onChange={e => set('name', e.target.value)} required />
          <input className="field-input" type="email" placeholder="Email address *" value={values.email} onChange={e => set('email', e.target.value)} required />
          <input className="field-input" type="tel" placeholder="Phone number (optional)" value={values.phone} onChange={e => set('phone', e.target.value)} />
          <textarea className="field-textarea" rows={3} placeholder={fields.messagePlaceholder} value={values.message} onChange={e => set('message', e.target.value)} />
          <div className="modal-btns">
            <button type="button" className="btn-modal secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-modal primary">{fields.submitLabel}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StorefrontView({ items, onMarkPending }) {
  const [query, setQuery] = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [aiKeywords, setAiKeywords] = useState(null); // null = not used, [] = used but no results
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState('');

  const available = items.filter(i => i.status === 'available');

  // Filter items against a list of keywords
  const filterByKeywords = (keywords) => {
    if (!keywords || keywords.length === 0) return available;
    return available.filter(item => {
      const haystack = [item.title, item.description, item.tags, item.category]
        .filter(Boolean).join(' ').toLowerCase();
      return keywords.some(kw => haystack.includes(kw.toLowerCase()));
    });
  };

  // Instant text filter (no AI)
  const instantFiltered = query.trim()
    ? filterByKeywords(query.trim().split(/\s+|,/).filter(Boolean))
    : available;

  // AI natural language search
  const handleAiSearch = async () => {
    if (!query.trim()) return;
    setAiSearching(true);
    setAiKeywords(null);
    try {
      const response = await fetch(ANTHROPIC_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_KEY || '',
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 200,
          messages: [{
            role: 'user',
            content: `Extract search keywords from this yard sale search query. Return ONLY a JSON array of lowercase keyword strings — no markdown, no explanation. Include synonyms and related terms. Query: "${query}"`
          }]
        })
      });
      const data = await response.json();
      const raw = data.content.map(b => b.text || '').join('').trim();
      const keywords = JSON.parse(raw.replace(/```json|```/g, '').trim());
      setAiKeywords(keywords);
    } catch (err) {
      console.error('AI search error:', err);
    } finally {
      setAiSearching(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setAiKeywords(null);
  };

  // Decide which items to show
  const displayedItems = aiKeywords !== null
    ? filterByKeywords(aiKeywords)
    : instantFiltered;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const handleBuySubmit = () => {
    onMarkPending(modal.item.id);
    setModal(null);
    showToast('✓ Purchase request sent! The seller will contact you.');
  };

  const handleInspectSubmit = () => {
    setModal(null);
    showToast('✓ Inspection request received! The seller will reach out to arrange a time.');
  };

  return (
    <div className="store-wrap">
      <div className="store-hero">
        <div className="store-hero-inner">
          <h1 className="store-title">Paseo Yard Sale</h1>
          <p className="store-subtitle">Browse items for sale in the Paseo Arts district. Buy online or request an in-person look before you decide.</p>
          <div className="store-meta">
            <span>📍 Paseo Arts District, Oklahoma City</span>
            <span>{available.length} item{available.length !== 1 ? 's' : ''} available</span>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="search-bar-wrap">
        <div className="search-bar">
          <input
            className="search-input"
            type="text"
            placeholder="Search items… e.g. leather jacket, cast iron, vintage camera"
            value={query}
            onChange={e => { setQuery(e.target.value); setAiKeywords(null); }}
            onKeyDown={e => e.key === 'Enter' && handleAiSearch()}
          />
          {query && (
            <button className="search-clear" onClick={clearSearch} title="Clear search">✕</button>
          )}
          <button
            className="search-ai-btn"
            onClick={handleAiSearch}
            disabled={aiSearching || !query.trim()}
            title="AI-assisted search — understands natural language"
          >
            {aiSearching ? <span className="search-spinner" /> : '✨ AI search'}
          </button>
        </div>
        {aiKeywords !== null && (
          <div className="search-status">
            AI found keywords: {aiKeywords.map(k => (
              <span key={k} className="keyword-chip">{k}</span>
            ))}
            <button className="search-status-clear" onClick={clearSearch}>Clear</button>
          </div>
        )}
        {query.trim() && displayedItems.length === 0 && (
          <div className="search-no-results">No items matched "{query}"</div>
        )}
      </div>

      <div className="store-body">
        {available.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🛒</div>
            <h3>No items listed yet</h3>
            <p>Check back soon!</p>
          </div>
        ) : (
          <div className="store-grid">
            {displayedItems.map(item => (
              <div key={item.id} className="store-card">
                {item.image_url ? (
                  <div className="store-card-img-wrap">
                    <img src={item.image_url} alt={item.title} className="store-card-img" />
                  </div>
                ) : (
                  <div className="store-card-img-wrap store-card-no-img">No photo</div>
                )}
                <div className="store-card-body">
                  <div className="store-card-category">{item.category}</div>
                  <div className="store-card-title">{item.title}</div>
                  <p className="store-card-desc">{item.description}</p>
                  <div className="store-card-footer">
                    <span className="store-price">${Number(item.price).toFixed(2)}</span>
                    <div className="store-actions">
                      <button className="action-btn inspect" onClick={() => setModal({ type: 'inspect', item })}>
                        Inspect in person
                      </button>
                      <button className="action-btn buy" onClick={() => setModal({ type: 'buy', item })}>
                        Buy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal?.type === 'buy' && (
        <Modal
          title="Purchase this item"
          item={modal.item}
          fields={{ messagePlaceholder: 'Any message for the seller?', submitLabel: 'Submit purchase request' }}
          onSubmit={handleBuySubmit}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === 'inspect' && (
        <Modal
          title="Request an inspection"
          item={modal.item}
          fields={{ messagePlaceholder: 'Preferred times, or any questions?', submitLabel: 'Send inspection request' }}
          onSubmit={handleInspectSubmit}
          onClose={() => setModal(null)}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
